import { useAuthStore, useThreadStore } from "@/store";
import { useCanvas } from "@/contexts/CanvasContext";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Centralized hook for handling new chat creation across different contexts
 * @param {Object} options - Configuration options for different contexts
 * @param {Function} options.onBeforeNavigate - Custom cleanup function called before navigation
 * @param {Array} options.sidebarsToClose - Array of sidebar setter functions to close
 * @param {Function} options.additionalCleanup - Additional cleanup function
 * @param {boolean} options.closeCanvas - Whether to close canvas (for CanvasTopbar)
 * @param {Function} options.closeCanvasFunction - Function to close canvas
 * @returns {Object} - Object containing handleNewChat function and loading state
 */
export const useNewChat = (options = {}) => {
  const {
    onBeforeNavigate,
    sidebarsToClose = [],
    additionalCleanup,
    closeCanvas = false,
    closeCanvasFunction,
  } = options;

  const navigate = useNavigate();
  const navigatingRef = useRef(false);
  const [isCreatingChat, setIsCreatingChat] = useState(false);

  // Get canvas state to check for unsaved changes
  const { canvasState } = useCanvas();

  // Track creation to prevent duplicates
  const creationInProgressRef = useRef(false);
  const lastCreatedThreadRef = useRef(null);

  // Cleanup effect to reset creation flag if it gets stuck
  useEffect(() => {
    // Reset creation flag after 10 seconds if it's still true
    const timeoutId = setInterval(() => {
      if (creationInProgressRef.current) {
        const lastCreated = lastCreatedThreadRef.current;
        if (!lastCreated || Date.now() - lastCreated.timestamp > 10000) {
          console.warn(
            "⚠️ [useNewChat] Resetting stuck creation flag after 10 seconds"
          );
          creationInProgressRef.current = false;
        }
      }
    }, 10000);

    return () => clearInterval(timeoutId);
  }, []);

  /**
   * Centralized function to close all sidebars
   * Uses both the provided sidebar functions and global events for comprehensive coverage
   */
  const closeAllSidebars = useCallback(() => {
    try {
      // Close sidebars via provided functions (if available)
      sidebarsToClose.forEach((closeSidebar) => {
        if (typeof closeSidebar === "function") {
          closeSidebar(false);
        }
      });

      // Dispatch global event to close all sidebars
      // This ensures that all sidebars close regardless of context availability
      window.dispatchEvent(
        new CustomEvent("close-all-sidebars", {
          detail: { source: "new-chat" },
        })
      );

      console.log("🔄 All sidebars closed for new chat");
    } catch (error) {
      console.warn("⚠️ Error closing sidebars:", error);
    }
  }, [sidebarsToClose]);

  // Function to create thread and vector store (centralized logic)
  const createThreadAndVectorStore = useCallback(
    async (userId, currentWorkspaceId, source = "button") => {
      try {
        // Check if we already have a pending thread to prevent duplicates
        const { getPendingNewChatThread } = useThreadStore.getState();
        const existingPendingThread = getPendingNewChatThread();

        if (existingPendingThread) {
          console.log(
            `🔄 [useNewChat] Using existing pending thread: ${existingPendingThread} (source: ${source})`
          );
          return existingPendingThread;
        }

        // Check if we just created a thread recently (within 2 seconds)
        if (
          lastCreatedThreadRef.current &&
          Date.now() - lastCreatedThreadRef.current.timestamp < 2000
        ) {
          console.log(
            `🔄 [useNewChat] Preventing duplicate - thread ${lastCreatedThreadRef.current.threadId} was just created ${Date.now() - lastCreatedThreadRef.current.timestamp}ms ago`
          );
          return lastCreatedThreadRef.current.threadId;
        }

        // Additional check: Prevent concurrent creation attempts
        if (creationInProgressRef.current) {
          console.log(
            `⚠️ [useNewChat] Thread creation already in progress, skipping duplicate attempt (source: ${source})`
          );
          return null;
        }

        creationInProgressRef.current = true;

        const {
          createThread,
          setCurrentThread,
          setCurrentThreadId,
          setPendingNewChatThread,
        } = useThreadStore.getState();

        const newThread = await createThread(
          userId,
          null, // assistantId - will be set when first message is sent
          "New Chat", // title - will be updated when first message is sent
          "", // initialContext
          {
            workspace_id: currentWorkspaceId,
            metadata: {
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              is_active: true,
              needs_title_generation: true,
              created_via: source,
            },
          }
        );

        if (newThread?.id) {
          // Track this creation to prevent duplicates
          lastCreatedThreadRef.current = {
            threadId: newThread.id,
            timestamp: Date.now(),
          };

          // Set the thread as current
          setCurrentThread(newThread);
          setCurrentThreadId(newThread.id);

          // Store the thread ID in thread store for ChatContext to use
          setPendingNewChatThread(newThread.id);

          // ADDED: Dispatch thread-changed event to notify components
          try {
            window.dispatchEvent(
              new CustomEvent("thread-changed", {
                detail: {
                  threadId: newThread.id,
                  source: source,
                  isNewThread: true,
                },
              })
            );
          } catch (error) {
            console.warn("⚠️ Error dispatching thread-changed event:", error);
          }

          console.log(
            `✅ [useNewChat] Created and set pending thread: ${newThread.id} (source: ${source})`
          );

          // CRITICAL: Create vector store in background and link to thread
          setTimeout(async () => {
            try {
              // Check if thread already has a vector store to prevent duplicates
              const { getThreadVectorStore } = useThreadStore.getState();
              const existingVectorStore = getThreadVectorStore(newThread.id);

              if (existingVectorStore) {
                return;
              }

              // Import vector store utilities
              const { createVectorStore } = await import(
                "@/lib/utils/vectorStoreUtils"
              );

              // Create vector store with descriptive name
              const vectorStoreName = `Thread_${newThread.id}_${Date.now()}`;
              const vectorStoreId = await createVectorStore(vectorStoreName);

              if (vectorStoreId) {
                // Update thread store with vector store ID
                const { setThreadVectorStore } = useThreadStore.getState();
                setThreadVectorStore(newThread.id, vectorStoreId);

                // Update thread in database with vector store ID
                try {
                  const { supabase } = await import("@/lib/supabase");
                  const { error: updateError } = await supabase
                    .from("threads")
                    .update({
                      vector_store_id: vectorStoreId,
                      metadata: {
                        ...newThread.metadata,
                        vector_store_created_at: new Date().toISOString(),
                        vector_store_name: vectorStoreName,
                      },
                    })
                    .eq("id", newThread.id);

                  if (updateError) {
                    console.error(
                      `❌ Failed to update thread ${newThread.id} with vector store ID:`,
                      updateError
                    );
                  }
                } catch (dbError) {
                  console.error(
                    `❌ Database error updating thread ${newThread.id}:`,
                    dbError
                  );
                }
              } else {
                console.warn(
                  `⚠️ Vector store creation failed for thread ${newThread.id}`
                );
              }
            } catch (vectorStoreError) {
              console.error(
                `❌ Vector store creation error for thread ${newThread.id}:`,
                vectorStoreError
              );
            }
          }, 200); // Additional delay for vector store creation

          return newThread.id;
        } else {
          console.warn(`⚠️ Thread creation failed (via ${source})`);
          return null;
        }
      } catch (error) {
        console.error(`❌ Error creating thread (via ${source}):`, error);
        return null;
      } finally {
        creationInProgressRef.current = false;
      }
    },
    []
  );

  // Function to handle direct navigation to /chat/new
  const handleDirectNewChatNavigation = useCallback(async () => {
    if (creationInProgressRef.current) {
      console.log(
        "⚠️ [useNewChat] Direct navigation - thread creation already in progress, skipping"
      );
      return;
    }

    try {
      // Dispatch new-chat-started event for direct navigation
      try {
        window.dispatchEvent(
          new CustomEvent("new-chat-started", {
            detail: {
              source: "direct_navigation",
              timestamp: new Date().toISOString(),
            },
          })
        );
      } catch (error) {
        console.warn("⚠️ Error dispatching new-chat-started event:", error);
      }

      const { userId, currentWorkspaceId } = useAuthStore.getState();

      if (userId) {
        const threadId = await createThreadAndVectorStore(
          userId,
          currentWorkspaceId,
          "direct_navigation"
        );
        return threadId;
      }
    } catch (error) {
      console.error("❌ Error handling direct /chat/new navigation:", error);
    }
  }, [createThreadAndVectorStore]);

  const handleNewChat = useCallback(async () => {
    // Prevent multiple simultaneous operations
    if (navigatingRef.current) {
      return;
    }
    navigatingRef.current = true;

    try {
      setIsCreatingChat(true);

      // Execute custom cleanup before navigation
      if (onBeforeNavigate) {
        await onBeforeNavigate();
      }

      // CRITICAL: Check for unsaved canvas changes BEFORE clearing thread state
      // This prevents losing the thread if user cancels the confirmation dialog
      if (canvasState.isOpen && canvasState.isDirty) {
        const confirmed = window.confirm(
          "You have unsaved changes in the canvas. Are you sure you want to close it?"
        );
        if (!confirmed) {
          // User cancelled - abort navigation and keep current thread
          navigatingRef.current = false;
          setIsCreatingChat(false);
          return;
        }
      }

      // CRITICAL: Close all sidebars first using centralized function
      closeAllSidebars();

      // Close canvas if specified
      if (closeCanvas && closeCanvasFunction) {
        closeCanvasFunction();
      }

      // Clear deep research state for new chat
      try {
        window.dispatchEvent(
          new CustomEvent("clear-deep-research-state", {
            detail: { source: "new-chat" },
          })
        );
        console.log(
          "🔬 Dispatched clear-deep-research-state event for new chat"
        );
      } catch (error) {
        console.warn(
          "⚠️ Error dispatching clear-deep-research-state event:",
          error
        );
      }

      // Dispatch new-chat-started event to reset all tool states
      try {
        window.dispatchEvent(
          new CustomEvent("new-chat-started", {
            detail: {
              source: "new-chat-button",
              timestamp: new Date().toISOString(),
            },
          })
        );
      } catch (error) {
        console.warn("⚠️ Error dispatching new-chat-started event:", error);
      }

      // Clear the current thread state
      const { clearCurrentThread, setMessages } = useThreadStore.getState();
      clearCurrentThread();

      // Set messages to empty array explicitly
      setMessages([]);

      // Clear current files from file storage store
      try {
        const { useFileStorageStore } = await import(
          "@/store/fileStorageStore"
        );
        const { clearCurrentFiles } = useFileStorageStore.getState();
        clearCurrentFiles();
      } catch (fileStorageError) {
        console.warn("⚠️ Error clearing current files:", fileStorageError);
      }

      // Clear messages from localStorage if present
      localStorage.removeItem("previousMessages");
      localStorage.removeItem("pendingWorkspaceChat");
      localStorage.removeItem("pendingVectorStoreId");

      // Clear any existing new chat thread state in thread store
      const { clearPendingNewChatThread } = useThreadStore.getState();
      clearPendingNewChatThread();

      // Execute additional cleanup if provided
      if (additionalCleanup) {
        await additionalCleanup();
      }

      // HYBRID APPROACH: Navigate to /chat/new but create thread in background
      // This maintains the expected UX while optimizing thread creation
      try {
        const { userId, currentWorkspaceId } = useAuthStore.getState();

        // Always navigate to /chat/new first for consistent UX
        navigate("/chat/new");

        // IMPORTANT: Don't create thread here - let ChatPage handle it
        // The ChatPage component will call handleDirectNewChatNavigation
        // when it detects navigation to /chat/new, preventing duplicates

        // Just log for debugging
        console.log(
          "🔄 Navigated to /chat/new - thread creation will be handled by ChatPage"
        );
      } catch (error) {
        console.error("❌ Error in new chat flow:", error);
        // Still navigate to /chat/new as fallback
        navigate("/chat/new");
      }

      // Reset navigation lock after a short delay
      setTimeout(() => {
        navigatingRef.current = false;
      }, 300);
    } catch (error) {
      console.error("Failed to create new chat:", error);
      navigatingRef.current = false;
      // Fallback navigation in case of error
      navigate("/chat/new");
    } finally {
      setIsCreatingChat(false);
    }
  }, [
    navigate,
    onBeforeNavigate,
    closeAllSidebars,
    additionalCleanup,
    closeCanvas,
    closeCanvasFunction,
    canvasState.isOpen,
    canvasState.isDirty,
  ]);

  return {
    handleNewChat,
    handleDirectNewChatNavigation,
    isCreatingChat,
  };
};

export default useNewChat;
